import React from "react";
import { useStateListRequest, useAreaListCityRequest } from "src/hooks/api/area";
import { ChevronDown, MapPin, X, Search } from "lucide-react";
import { formatAddressWithStateAbbreviation } from "src/lib/utils";
import { AddressSearchProps } from "src/components/address-search/AddressSearch";
import { Input } from "src/components/ui/input";
import { Button } from "src/components/ui/button";

export type Option = {
  label: string;
  value: google.maps.LatLngLiteral;
  type: "address" | "city" | "state";
  placeId: string;
};

export interface AddressDropdownValue {
  state?: string;
  city?: string;
  stateId?: number;
  cityId?: number;
}

const MobileAddressDropdown: React.FC<AddressSearchProps> = ({
  initialValue,
  initialOption,
  onChange,
  onValueChange,
  variant = "dropdown",
  searchTypes = ["(cities)"],
  enableDefaultOptions = true,
  maxResults = 6,
  placeholder = "Search state, city...",
  useStateAbbreviation = false,
}) => {
  const [inputValue, setInputValue] = React.useState<string>("");
  const [selectedState, setSelectedState] = React.useState<number | undefined>();
  const [selectedCity, setSelectedCity] = React.useState<number | undefined>();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [activeSection, setActiveSection] = React.useState<"states" | "cities" | "both">("both");

  // Track user interaction and initial option processing
  const hasUserInteracted = React.useRef<boolean>(false);
  const initialOptionProcessed = React.useRef<boolean>(false);

  // Helper function to conditionally format address based on prop
  const formatAddressLabel = React.useCallback(
    (address: string) => (useStateAbbreviation ? formatAddressWithStateAbbreviation(address) : address),
    [useStateAbbreviation],
  );
  const [googleCities, setGoogleCities] = React.useState<Array<{ id: string; name: string; placeId: string }>>([]);
  const [selectedGoogleCity, setSelectedGoogleCity] = React.useState<{ name: string; placeId: string } | null>(null);
  const [selectedGoogleAddress, setSelectedGoogleAddress] = React.useState<{ description: string; placeId: string } | null>(null);
  const [googleAddresses, setGoogleAddresses] = React.useState<Array<{ id: string; description: string; placeId: string }>>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Google Maps instances
  const geocoderInstance = React.useMemo(() => new google.maps.Geocoder(), []);
  const autocompleteService = React.useMemo(() => new google.maps.places.AutocompleteService(), []);
  const sessionToken = React.useMemo(() => new google.maps.places.AutocompleteSessionToken(), []);

  const { data: stateData } = useStateListRequest();

  const cityParams = React.useMemo(
    () => ({
      state: selectedState || undefined,
      level: "3",
      limit: 500,
      unique: true,
    }),
    [selectedState],
  );

  const { data: cityData } = useAreaListCityRequest(cityParams, !!selectedState);

  const cities = React.useMemo(() => {
    if (!cityData?.data.results) {
      return [];
    }
    return cityData.data.results;
  }, [cityData]);

  const states = React.useMemo(() => {
    if (!stateData?.data) {
      return [];
    }
    return stateData.data;
  }, [stateData]);

  // Lock body scroll when overlay is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Initialize component with initialOption if provided (only once)
  React.useEffect(() => {
    if (initialOption && !initialOptionProcessed.current && !hasUserInteracted.current) {
      // Parse the initial option to extract state and city information
      const parseInitialOption = async () => {
        try {
          if (initialOption.placeId) {
            // Use Google Places API to get detailed information
            const geocodeResult = await geocoderInstance.geocode({ placeId: initialOption.placeId });
            if (geocodeResult.results.length > 0) {
              const result = geocodeResult.results[0];
              let foundState: any = null;
              let foundCity: any = null;
              let cityName: string | null = null;
              let stateName: string | null = null;

              // Extract state and city from address components using array methods
              result.address_components.forEach((component) => {
                if (component.types.includes("administrative_area_level_1")) {
                  stateName = component.long_name;
                }
                if (component.types.includes("locality") || component.types.includes("sublocality")) {
                  cityName = component.long_name;
                }
              });

              // Find matching state in our database
              if (stateName) {
                foundState = states.find(
                  (state) =>
                    state.name.toLowerCase() === stateName!.toLowerCase() ||
                    state.name.toLowerCase().includes(stateName!.toLowerCase()) ||
                    stateName!.toLowerCase().includes(state.name.toLowerCase()),
                );
              }

              // If we found a state, look for the city in that state's cities
              if (foundState && cityName) {
                foundCity = cities.find(
                  (city) =>
                    city.name.toLowerCase() === cityName!.toLowerCase() ||
                    city.name.toLowerCase().includes(cityName!.toLowerCase()) ||
                    cityName!.toLowerCase().includes(city.name.toLowerCase()),
                );
              }

              if (foundState) {
                setSelectedState(typeof foundState.id === "number" ? foundState.id : parseInt(String(foundState.id), 10));
                if (foundCity) {
                  setSelectedCity(typeof foundCity.id === "number" ? foundCity.id : parseInt(String(foundCity.id), 10));
                } else if (cityName) {
                  // Set as Google city if not found in database
                  setSelectedGoogleCity({ name: cityName, placeId: initialOption.placeId });
                }
              } else if (cityName && stateName) {
                // Set as Google address if we can't find state in database
                setSelectedGoogleAddress({ description: initialOption.label, placeId: initialOption.placeId });
              }
            }
          } else if (initialOption.value) {
            // Fallback: try to parse from coordinates
            const geocodeResult = await geocoderInstance.geocode({
              location: initialOption.value,
              componentRestrictions: { country: "us" },
            });

            if (geocodeResult.results.length > 0) {
              const result = geocodeResult.results[0];
              let foundState: any = null;
              let foundCity: any = null;
              let cityName: string | null = null;
              let stateName: string | null = null;

              // Extract state and city from address components using array methods
              result.address_components.forEach((component) => {
                if (component.types.includes("administrative_area_level_1")) {
                  stateName = component.long_name;
                }
                if (component.types.includes("locality") || component.types.includes("sublocality")) {
                  cityName = component.long_name;
                }
              });

              // Find matching state in our database
              if (stateName) {
                foundState = states.find(
                  (state) =>
                    state.name.toLowerCase() === stateName!.toLowerCase() ||
                    state.name.toLowerCase().includes(stateName!.toLowerCase()) ||
                    stateName!.toLowerCase().includes(state.name.toLowerCase()),
                );
              }

              // If we found a state, look for the city in that state's cities
              if (foundState && cityName) {
                foundCity = cities.find(
                  (city) =>
                    city.name.toLowerCase() === cityName!.toLowerCase() ||
                    city.name.toLowerCase().includes(cityName!.toLowerCase()) ||
                    cityName!.toLowerCase().includes(city.name.toLowerCase()),
                );
              }

              if (foundState) {
                setSelectedState(typeof foundState.id === "number" ? foundState.id : parseInt(String(foundState.id), 10));
                if (foundCity) {
                  setSelectedCity(typeof foundCity.id === "number" ? foundCity.id : parseInt(String(foundCity.id), 10));
                } else if (cityName) {
                  // Set as Google city if not found in database
                  setSelectedGoogleCity({ name: cityName, placeId: result.place_id });
                }
              } else if (cityName && stateName) {
                // Set as Google address if we can't find state in database
                setSelectedGoogleAddress({ description: initialOption.label, placeId: result.place_id });
              }
            }
          }
        } catch (error) {
          console.error("Error parsing initial option:", error);
        }
      };

      parseInitialOption();
      initialOptionProcessed.current = true;
    }
  }, [initialOption, states, cities, geocoderInstance]);

  // Filter states and cities based on input
  const filteredStates = React.useMemo(() => {
    if (!inputValue.trim()) return states;
    return states.filter((state) => {
      const matchesSearch = state.name.toLowerCase().includes(inputValue.toLowerCase());
      const isSelected = selectedState === state.id;
      return matchesSearch || isSelected;
    });
  }, [states, inputValue, selectedState]);

  const filteredCities = React.useMemo(() => {
    if (!inputValue.trim()) return cities;
    // Simple filtering: just filter cities by the input value, but always show selected city
    return cities.filter((city) => {
      const matchesSearch = city.name.toLowerCase().includes(inputValue.toLowerCase());
      const isSelected = selectedCity === city.id;
      return matchesSearch || isSelected;
    });
  }, [cities, inputValue, selectedCity]);

  // Search Google Places API for full addresses
  const searchGoogleAddresses = React.useCallback(
    async (input: string) => {
      if (!input.trim() || input.length < 3) {
        setGoogleAddresses([]);
        return;
      }

      setIsLoading(true);
      try {
        const selectedStateName = states.find((s) => s.id === selectedState)?.name;

        // Smart input parsing
        let searchInput = input;
        if (selectedStateName) {
          const inputParts = input.split(",").map((part) => part.trim());
          if (inputParts.length > 1) {
            const cityName = inputParts[0]; // "Denver"
            const stateInInput = inputParts[1]; // "Colorado"

            // If the state in input matches selected state, use just the city name
            searchInput = stateInInput && stateInInput.toLowerCase() === selectedStateName.toLowerCase() ? cityName : input;
          } else {
            // User just typed "Denver", search within selected state
            searchInput = `${input}, ${selectedStateName}`;
          }
        }

        const predictions = await autocompleteService.getPlacePredictions({
          input: searchInput,
          sessionToken,
          types: ["(cities)"],
          componentRestrictions: { country: "us" },
        });

        if (predictions.predictions) {
          const addressResults = predictions.predictions
            .slice(0, 8)
            // Filter out cities that already exist in database (case insensitive comparison)
            .filter((prediction) => {
              if (!selectedState) {
                return true;
              }

              // Extract city name from the description (usually the first part before comma)
              const cityName = prediction.description.split(",")[0].trim();

              // Extract state name from the description (usually the second part after comma)
              const stateNameFromGoogle = prediction.description.split(",")[1]?.trim();

              // Get the selected state name for comparison
              const selectedStateNametemp = states.find((s) => s.id === selectedState)?.name;

              // Check if this city already exists in our database
              const existsInDatabase = cities.some((dbCity) => dbCity.name.toLowerCase().trim() === cityName.toLowerCase().trim());

              // Also check if it's a Google city that's already shown
              const existsInGoogleCities = googleCities.some(
                (googleCity) => googleCity.name.toLowerCase().trim() === cityName.toLowerCase().trim(),
              );

              // Check if it's a city-type prediction that matches existing database cities
              const isCityType = prediction.types.includes("locality") || prediction.types.includes("sublocality");

              // Only filter out if it's a city-type AND exists in database AND matches the selected state
              const isCityMatch =
                isCityType &&
                existsInDatabase &&
                selectedStateNametemp &&
                stateNameFromGoogle &&
                stateNameFromGoogle.toLowerCase().includes(selectedStateNametemp.toLowerCase());

              return !existsInDatabase && !existsInGoogleCities && !isCityMatch;
            })
            .map((prediction) => ({
              id: `address-${prediction.place_id}`,
              description: prediction.description,
              placeId: prediction.place_id,
            }));

          setGoogleAddresses(addressResults);
        } else {
          setGoogleAddresses([]);
        }
      } catch (error) {
        console.error("Error searching Google addresses:", error);
        setGoogleAddresses([]);
      } finally {
        setIsLoading(false);
      }
    },
    [autocompleteService, sessionToken, selectedState, states, cities, googleCities],
  );

  // Search Google Places API for cities when state is selected and user is typing
  const searchGoogleCities = React.useCallback(
    async (input: string, stateName: string) => {
      if (!input.trim() || input.length < 2) {
        setGoogleCities([]);
        return;
      }

      try {
        const predictions = await autocompleteService.getPlacePredictions({
          input: `${input}, ${stateName}`,
          sessionToken,
          types: ["(cities)"],
          componentRestrictions: { country: "us" },
        });

        if (predictions.predictions) {
          const googleCityResults = predictions.predictions
            .filter(
              (prediction) =>
                prediction.types.includes("locality") && prediction.description.toLowerCase().includes(stateName.toLowerCase()),
            )
            .slice(0, 10) // Get more results initially for filtering
            .map((prediction, index) => ({
              id: `google-${prediction.place_id}`,
              name: prediction.structured_formatting.main_text,
              placeId: prediction.place_id,
            }))
            // Filter out cities that already exist in database (case insensitive comparison)
            .filter((googleCity) => {
              const existsInDatabase = cities.some((dbCity) => dbCity.name.toLowerCase().trim() === googleCity.name.toLowerCase().trim());
              return !existsInDatabase;
            })
            .slice(0, 5); // Limit to 5 unique results after filtering

          setGoogleCities(googleCityResults);
        } else {
          setGoogleCities([]);
        }
      } catch (error) {
        console.error("Error searching Google cities:", error);
        setGoogleCities([]);
      }
    },
    [autocompleteService, sessionToken, cities],
  );

  // Debounced version of Google address search
  const debouncedGoogleAddressSearch = React.useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (input: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => searchGoogleAddresses(input), 300);
    };
  }, [searchGoogleAddresses]);

  // Debounced version of Google city search
  const debouncedGoogleCitySearch = React.useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (input: string, stateName: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => searchGoogleCities(input, stateName), 300);
    };
  }, [searchGoogleCities]);

  // Helper function to parse Google address and find matching state/city
  const parseGoogleAddress = React.useCallback(
    async (address: { description: string; placeId: string }) => {
      try {
        // First get detailed place information
        const geocodeResult = await geocoderInstance.geocode({ placeId: address.placeId });

        if (geocodeResult.results.length > 0) {
          const result = geocodeResult.results[0];
          let foundState = null;
          let foundCity = null;
          let cityName: string | null = null;
          let stateName: string | null = null;

          // Extract state and city from address components
          result.address_components.forEach((component) => {
            if (component.types.includes("administrative_area_level_1")) {
              stateName = component.long_name;
            }
            if (component.types.includes("locality") || component.types.includes("sublocality")) {
              cityName = component.long_name;
            }
          });

          // Find matching state in our database
          if (stateName) {
            const stateNameLower = (stateName as string).toLowerCase();
            foundState = states.find(
              (state) =>
                state.name.toLowerCase() === stateNameLower ||
                state.name.toLowerCase().includes(stateNameLower) ||
                stateNameLower.includes(state.name.toLowerCase()),
            );
          }

          // If we found a state, look for the city in that state's cities
          if (foundState && cityName) {
            // For now, check if the city exists in currently loaded cities
            // In a real implementation, you might want to make an API call here
            const cityNameLower = (cityName as string).toLowerCase();
            foundCity = cities.find(
              (city) =>
                city.name.toLowerCase() === cityNameLower ||
                city.name.toLowerCase().includes(cityNameLower) ||
                cityNameLower.includes(city.name.toLowerCase()),
            );
          }

          return {
            foundState,
            foundCity,
            stateName,
            cityName,
            coordinates: {
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
            },
          };
        }
      } catch (error) {
        console.error("Error parsing Google address:", error);
      }

      return null;
    },
    [geocoderInstance, states, cities],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);

    // Mark that user has interacted with the component
    hasUserInteracted.current = true;

    // Always search for full addresses when user types
    if (value.trim() && value.length >= 3) {
      debouncedGoogleAddressSearch(value);
    } else {
      setGoogleAddresses([]);
    }

    // Simple and clean: if user is typing, show appropriate sections based on current selection
    if (selectedState && !selectedCity && !selectedGoogleCity && !selectedGoogleAddress) {
      // State selected, no city - focus on cities
      setActiveSection("cities");

      // Search Google Places API for cities in the selected state
      const selectedStateName = states.find((s) => s.id === selectedState)?.name;
      if (selectedStateName && value.trim()) {
        debouncedGoogleCitySearch(value, selectedStateName);
      } else {
        setGoogleCities([]);
      }
    } else if (!selectedState && !selectedGoogleAddress) {
      // No state/address selected - show both
      setActiveSection("both");
      setGoogleCities([]);
    } else {
      // Something selected - user probably wants to search more, show both
      setActiveSection("both");
      setGoogleCities([]);
    }
  };

  const handleStateSelect = (state: any) => {
    setSelectedState(state.id);
    setSelectedCity(undefined);
    setInputValue(""); // Clear input since state is now in chip
    setActiveSection("cities");
    setGoogleAddresses([]);
  };

  const handleCitySelect = async (city: any) => {
    const state = states.find((s) => s.id === selectedState);
    setSelectedCity(city.id);
    setSelectedGoogleCity(null); // Clear Google city selection
    setInputValue(""); // Clear input since city is now in chip
    setGoogleCities([]); // Clear Google cities
    setIsOpen(false);

    try {
      // Use Google Maps to get city coordinates and placeId
      const geocodeResult = await geocoderInstance.geocode({
        componentRestrictions: {
          country: "us",
          locality: city.name,
          administrativeArea: state?.name,
        },
      });

      if (geocodeResult.results.length > 0) {
        const { location } = geocodeResult.results[0].geometry;
        const latLngValue = {
          lat: location.lat(),
          lng: location.lng(),
        };

        const newOption: Option = {
          label: formatAddressLabel(`${city.name}, ${state?.name}`),
          value: latLngValue,
          type: "city",
          placeId: geocodeResult.results[0].place_id,
        };

        onChange?.(latLngValue);
        onValueChange?.(newOption);
      }
    } catch (error) {
      console.error("Error geocoding city:", error);
    }
  };

  const handleGoogleCitySelect = async (googleCity: { id: string; name: string; placeId: string }) => {
    const state = states.find((s) => s.id === selectedState);
    setSelectedCity(undefined); // Clear database city selection
    setSelectedGoogleCity({ name: googleCity.name, placeId: googleCity.placeId }); // Set Google city
    setInputValue(""); // Clear input since city is now in chip
    setGoogleCities([]); // Clear Google cities
    setGoogleAddresses([]); // Clear Google addresses
    setIsOpen(false);

    try {
      // Use place_id to get city coordinates
      const geocodeResult = await geocoderInstance.geocode({ placeId: googleCity.placeId });

      if (geocodeResult.results.length > 0) {
        const { location } = geocodeResult.results[0].geometry;
        const latLngValue = {
          lat: location.lat(),
          lng: location.lng(),
        };

        const newOption: Option = {
          label: formatAddressLabel(`${googleCity.name}, ${state?.name}`),
          value: latLngValue,
          type: "city",
          placeId: googleCity.placeId,
        };

        onChange?.(latLngValue);
        onValueChange?.(newOption);
      }
    } catch (error) {
      console.error("Error geocoding Google city:", error);
    }
  };

  const handleGoogleAddressSelect = async (address: { id: string; description: string; placeId: string }) => {
    // Clear all selections initially
    setSelectedState(undefined);
    setSelectedCity(undefined);
    setSelectedGoogleCity(null);
    setSelectedGoogleAddress(null);
    setInputValue("");
    setGoogleCities([]);
    setGoogleAddresses([]);
    hasUserInteracted.current = true;
    setIsOpen(false);

    try {
      // Parse the Google address to extract state and city
      const parsed = await parseGoogleAddress(address);

      if (parsed?.foundState && parsed?.foundCity) {
        // We found both state and city in our database - use them
        setSelectedState(typeof parsed.foundState.id === "number" ? parsed.foundState.id : parseInt(String(parsed.foundState.id), 10));
        setSelectedCity(typeof parsed.foundCity.id === "number" ? parsed.foundCity.id : parseInt(String(parsed.foundCity.id), 10));

        const newOption: Option = {
          label: formatAddressLabel(`${parsed.foundCity.name}, ${parsed.foundState.name}`),
          value: parsed.coordinates,
          type: "city",
          placeId: address.placeId,
        };

        onChange?.(parsed.coordinates);
        onValueChange?.(newOption);
      } else if (parsed?.foundState && parsed?.cityName) {
        // We found the state but not the city in our database - use Google city
        setSelectedState(typeof parsed.foundState.id === "number" ? parsed.foundState.id : parseInt(String(parsed.foundState.id), 10));
        setSelectedGoogleCity({ name: parsed.cityName, placeId: address.placeId });

        const newOption: Option = {
          label: formatAddressLabel(`${parsed.cityName}, ${parsed.foundState.name}`),
          value: parsed.coordinates,
          type: "city",
          placeId: address.placeId,
        };

        onChange?.(parsed.coordinates);
        onValueChange?.(newOption);
      } else {
        // Fallback to treating it as a full address if we can't parse state/city
        setSelectedGoogleAddress({ description: address.description, placeId: address.placeId });

        const geocodeResult = await geocoderInstance.geocode({ placeId: address.placeId });
        if (geocodeResult.results.length > 0) {
          const { location } = geocodeResult.results[0].geometry;
          const latLngValue = {
            lat: location.lat(),
            lng: location.lng(),
          };

          const newOption: Option = {
            label: formatAddressLabel(address.description),
            value: latLngValue,
            type: "address",
            placeId: address.placeId,
          };

          onChange?.(latLngValue);
          onValueChange?.(newOption);
        }
      }
    } catch (error) {
      console.error("Error handling Google address selection:", error);
    }
  };

  const handleClearAll = () => {
    setSelectedState(undefined);
    setSelectedCity(undefined);
    setSelectedGoogleCity(null);
    setSelectedGoogleAddress(null);
    setInputValue("");
    setActiveSection("both");
    setGoogleCities([]);
    setGoogleAddresses([]);
    hasUserInteracted.current = true;
    onChange?.(undefined);
    onValueChange?.(null);
  };

  const displayValue = React.useMemo(() => {
    const stateName = states.find((s) => s.id === selectedState)?.name;
    let cityName: string | undefined;
    if (selectedGoogleAddress) {
      cityName = selectedGoogleAddress.description;
    } else if (selectedGoogleCity) {
      cityName = selectedGoogleCity.name;
    } else {
      cityName = cities.find((c) => c.id === selectedCity)?.name;
    }

    if (selectedGoogleAddress) {
      return selectedGoogleAddress.description;
    }

    if (stateName && cityName) {
      return `${cityName}, ${stateName}`;
    }

    if (stateName) {
      return stateName;
    }

    return placeholder;
  }, [selectedState, selectedCity, selectedGoogleCity, selectedGoogleAddress, states, cities, placeholder]);

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="
          w-full flex items-center gap-3 px-2 py-1.5 
          border border-border rounded-md
          bg-background hover:bg-muted active:bg-muted/80
          transition-all duration-200 ease-in-out
          text-left
        "
      >
        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className={`flex-1 text-sm ${selectedState || selectedGoogleAddress ? "text-foreground" : "text-muted-foreground"}`}>
          {displayValue}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </button>

      {/* Full Screen Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-background animate-in fade-in-0 duration-200"
          onClick={(e) => {
            // Close when clicking the backdrop
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div className="h-full flex flex-col safe-area animate-in slide-in-from-bottom duration-300">
            {/* Header with Safe Area Top */}
            <div className="px-4 pt-safe-or-4 py-2 border-b border-border flex-shrink-0 flex items-center justify-between bg-background">
              <h2 className="text-lg font-semibold tracking-normal">Search Location</h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-secondary rounded-md transition-colors active:bg-secondary/80"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search Input */}
            <div className="px-4 py-4 border-b border-border flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder={(() => {
                    if (selectedGoogleAddress) {
                      return "Change address...";
                    }
                    if (selectedState && (selectedCity || selectedGoogleCity)) {
                      return "Change cities...";
                    }
                    if (selectedState) {
                      return "Search cities...";
                    }
                    return placeholder;
                  })()}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setIsOpen(false);
                    }
                  }}
                  className="pl-10 h-12 text-base"
                  autoComplete="off"
                  autoFocus
                />
              </div>

              {/* Selected Chips */}
              {(selectedState || selectedCity || selectedGoogleCity || selectedGoogleAddress) && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {/* State Chip */}
                  {selectedState && (
                    <div className="flex items-center bg-black/80 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                      <span>{states.find((s) => s.id === selectedState)?.name}</span>
                      <button
                        type="button"
                        className="ml-2 hover:bg-primary/20 rounded-full p-0.5 focus:outline-none"
                        onClick={() => {
                          setSelectedState(undefined);
                          setSelectedCity(undefined);
                          setSelectedGoogleCity(null);
                          setSelectedGoogleAddress(null);
                          setInputValue("");
                          setActiveSection("both");
                          setGoogleCities([]);
                          setGoogleAddresses([]);
                          hasUserInteracted.current = true;
                          onChange?.(undefined);
                          onValueChange?.(null);
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}

                  {/* City/Address Chip */}
                  {(selectedCity || selectedGoogleCity || selectedGoogleAddress) && (
                    <div className="flex items-center bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-medium">
                      <span className="truncate max-w-[200px]">
                        {(() => {
                          if (selectedGoogleAddress) {
                            return selectedGoogleAddress.description;
                          }
                          if (selectedGoogleCity) {
                            return selectedGoogleCity.name;
                          }
                          return cities.find((c) => c.id === selectedCity)?.name || "Selected City";
                        })()}
                      </span>
                      <button
                        type="button"
                        className="ml-2 hover:bg-green-200 rounded-full p-0.5 focus:outline-none"
                        onClick={() => {
                          setSelectedCity(undefined);
                          setSelectedGoogleCity(null);
                          setSelectedGoogleAddress(null);
                          setInputValue("");
                          setActiveSection(selectedState ? "cities" : "both");
                          setGoogleCities([]);
                          setGoogleAddresses([]);
                          hasUserInteracted.current = true;
                          onChange?.(undefined);
                          onValueChange?.(null);
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Results Area */}
            <div className="flex-1 overflow-y-auto customScrollbar2">
              {/* Loading State */}
              {isLoading && (
                <div className="px-4 py-3 space-y-3">
                  <div className="h-4 bg-muted-foreground/20 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-muted-foreground/20 rounded animate-pulse w-2/3" />
                  <div className="h-4 bg-muted-foreground/20 rounded animate-pulse w-1/2" />
                </div>
              )}

              {/* States Section */}
              {(activeSection === "both" || activeSection === "states") && filteredStates.length > 0 && (
                <div className="py-2 border-b border-border/50">
                  <div className="px-4 py-2 text-xs font-semibold tracking-wide text-muted-foreground bg-secondary/30">STATES</div>
                  <div className="px-2">
                    {filteredStates.map((state) => (
                      <button
                        key={state.id}
                        type="button"
                        className={`
                          w-full px-4 py-3 text-left text-sm rounded-md
                          hover:bg-secondary/60 active:bg-secondary/70
                          transition-colors duration-150 ease-in-out 
                          ${selectedState === state.id ? "bg-secondary/90 font-medium" : ""}
                        `}
                        onClick={() => handleStateSelect(state)}
                      >
                        {state.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cities Section */}
              {(activeSection === "both" || activeSection === "cities") &&
                selectedState &&
                (filteredCities.length > 0 || googleCities.length > 0) && (
                  <div className="py-2 border-b border-border/50">
                    <div className="px-4 py-2 flex items-center justify-between bg-secondary/30">
                      <span className="text-xs font-semibold tracking-wide text-muted-foreground">
                        CITIES IN {states.find((s) => s.id === selectedState)?.name?.toUpperCase()}
                      </span>
                      {(selectedCity || selectedGoogleCity) && (
                        <button
                          type="button"
                          className="text-sm text-green-600 hover:text-green-800 font-medium"
                          onClick={() => {
                            setSelectedCity(undefined);
                            setSelectedGoogleCity(null);
                            setSelectedGoogleAddress(null);
                            setInputValue("");
                            setActiveSection("cities");
                            setGoogleCities([]);
                            setGoogleAddresses([]);
                            hasUserInteracted.current = true;
                            onChange?.(undefined);
                            onValueChange?.(null);
                          }}
                        >
                          Clear Cities
                        </button>
                      )}
                    </div>
                    <div className="px-2">
                      {/* Database Cities */}
                      {filteredCities.map((city) => (
                        <button
                          key={city.id}
                          type="button"
                          className={`
                            w-full px-4 py-3 text-left text-base rounded-lg
                            hover:bg-secondary/60 active:bg-secondary/70
                            transition-colors duration-150 ease-in-out
                            ${selectedCity === city.id ? "bg-secondary/90 font-medium" : ""}
                          `}
                          onClick={() => handleCitySelect(city)}
                        >
                          {city.name}
                        </button>
                      ))}

                      {/* Google Places API Cities */}
                      {googleCities.map((googleCity) => (
                        <button
                          key={googleCity.id}
                          type="button"
                          className={`
                            w-full px-4 py-3 text-left text-base rounded-lg
                            hover:bg-secondary/60 active:bg-secondary/70
                            transition-colors duration-150 ease-in-out
                            ${selectedGoogleCity?.placeId === googleCity.placeId ? "bg-secondary/90 font-medium" : ""}
                          `}
                          onClick={() => handleGoogleCitySelect(googleCity)}
                        >
                          {googleCity.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {/* Addresses Section */}
              {googleAddresses.length > 0 && (
                <div className="py-2 border-b border-border/50">
                  <div className="px-4 py-2 text-xs font-semibold tracking-wide text-muted-foreground bg-secondary/30">ADDRESSES</div>
                  <div className="px-2">
                    {googleAddresses.map((address) => (
                      <button
                        key={address.id}
                        type="button"
                        className="
                          w-full px-4 py-3 text-left text-base rounded-lg
                          hover:bg-secondary/60 active:bg-secondary/70
                          transition-colors duration-150 ease-in-out
                        "
                        onClick={() => handleGoogleAddressSelect(address)}
                      >
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <span>{address.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {!isLoading &&
                filteredStates.length === 0 &&
                filteredCities.length === 0 &&
                googleCities.length === 0 &&
                googleAddresses.length === 0 &&
                inputValue.trim() && (
                  <div className="px-4 py-8 text-center text-muted-foreground">No locations found for &quot;{inputValue}&quot;</div>
                )}

              {/* Helper Text */}
              {!inputValue.trim() && !selectedState && (
                <div className="px-4 py-4 text-sm text-muted-foreground text-center">
                  Type to search or select a state first, then a city
                </div>
              )}
            </div>

            {/* Bottom Actions with Safe Area Bottom */}
            {(selectedState || selectedCity || selectedGoogleCity || selectedGoogleAddress) && (
              <div className="px-4 py-2 pb-safe-or-4 border-t border-border flex-shrink-0 bg-background">
                <Button variant="outline" className="w-full h-12" onClick={handleClearAll}>
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MobileAddressDropdown;
